import {
  allGenderFilters,
  allGenreFilters,
  allPitchRangeCategories,
  Category,
  GenderFilter,
  GenreFilter,
  PitchRangeCategory,
} from "@/constants/selectArtistFilters";
import { useNamedStore } from "@/lib/zustand/useNamedStore";
import { ModelSortVariant } from "@/types/defs/modelSortVariant";

type State = {
  categoryShow: Category;
  genderFilterShow: Set<GenderFilter>;
  pitchRangeCategoryFilterShow: Set<PitchRangeCategory>;
  genreFilterShow: Set<GenreFilter>;
  searchString: string;
  sortVariant: ModelSortVariant;

  setCategoryShow: (category: Category) => void;

  toggleGenderFilterShow: (filter: GenderFilter) => void;

  togglePitchRangeCategoryFilterShow: (filter: PitchRangeCategory) => void;

  toggleGenreFilterShow: (filter: GenreFilter) => void;

  setSearchString: (search: string) => void;

  setSortVariant: (variant: ModelSortVariant) => void;

  reset: () => void;
};

export const useNamedSelectArtistFilters = useNamedStore<State>(
  () => (set) => ({
    categoryShow: "Royalty-free",
    genderFilterShow: new Set(allGenderFilters),
    pitchRangeCategoryFilterShow: new Set(allPitchRangeCategories),
    genreFilterShow: new Set(allGenreFilters),
    searchString: "",
    sortVariant: "default",

    setCategoryShow: (category) =>
      set(() => {
        return { categoryShow: category };
      }),

    toggleGenderFilterShow: (filter) =>
      set((state) => {
        // If all genders all selected, then we should only show the selected gender
        const allGendersSelected = allGenderFilters.every((gender) =>
          state.genderFilterShow.has(gender)
        );

        if (allGendersSelected) {
          return { genderFilterShow: new Set([filter]) };
        }

        // Otherwise, toggle the gender
        const newSet = new Set(state.genderFilterShow);
        if (newSet.has(filter)) {
          newSet.delete(filter);

          // If set is empty, then we should show all genders
          if (newSet.size === 0) {
            return { genderFilterShow: new Set(allGenderFilters) };
          }
        } else {
          newSet.add(filter);
        }
        return { genderFilterShow: newSet };
      }),

    togglePitchRangeCategoryFilterShow: (filter) =>
      set((state) => {
        // If all pitch range categories are selected, then we should only show the selected pitch range category
        const allPitchRangeCategoriesSelected = allPitchRangeCategories.every(
          (category) => state.pitchRangeCategoryFilterShow.has(category)
        );

        if (allPitchRangeCategoriesSelected) {
          return { pitchRangeCategoryFilterShow: new Set([filter]) };
        }

        // Otherwise, toggle the pitch range category
        const newSet = new Set(state.pitchRangeCategoryFilterShow);
        if (newSet.has(filter)) {
          newSet.delete(filter);

          // If set is empty, then we should show all pitch range categories
          if (newSet.size === 0) {
            return {
              pitchRangeCategoryFilterShow: new Set(allPitchRangeCategories),
            };
          }
        } else {
          newSet.add(filter);
        }
        return { pitchRangeCategoryFilterShow: newSet };
      }),

    toggleGenreFilterShow: (filter) =>
      set((state) => {
        // If all genres are selected, then we should only show the selected genre
        const allGenresSelected = allGenreFilters.every((genre) =>
          state.genreFilterShow.has(genre)
        );

        if (allGenresSelected) {
          return { genreFilterShow: new Set([filter]) };
        }

        // Otherwise, toggle the genre
        const newSet = new Set(state.genreFilterShow);
        if (newSet.has(filter)) {
          newSet.delete(filter);

          // If set is empty, then we should show all genres
          if (newSet.size === 0) {
            return { genreFilterShow: new Set(allGenreFilters) };
          }
        } else {
          newSet.add(filter);
        }

        return { genreFilterShow: newSet };
      }),

    setSearchString: (search) =>
      set(() => {
        return { searchString: search };
      }),

    setSortVariant: (variant) =>
      set(() => {
        return { sortVariant: variant };
      }),

    reset: () =>
      set(() => {
        return {
          categoryShow: "Royalty-free",
          genderFilterShow: new Set(allGenderFilters),
          pitchRangeCategoryFilterShow: new Set(allPitchRangeCategories),
          genreFilterShow: new Set(allGenreFilters),
          searchString: "",
          sortVariant: "default",
        };
      }),
  })
);
